import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { authApi, setAccessToken } from '../lib/api';
import { connectSocket } from '../lib/socket';
import { apiErrorMessage } from '../lib/apiError';

type View = 'login' | 'forgot_phone' | 'forgot_otp';

export default function LoginPage() {
  const [view, setView]           = useState<View>('login');
  const [phone, setPhone]         = useState('');
  const [password, setPassword]   = useState('');
  const [showPw, setShowPw]       = useState(false);
  const [otp, setOtp]             = useState('');
  const [newPw, setNewPw]         = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [error, setError]         = useState('');
  const [info, setInfo]           = useState('');
  const [loading, setLoading]     = useState(false);
  const login = useAuthStore((s) => s.login);
  const navigate = useNavigate();

  function reset(v: View) {
    setError(''); setInfo('');
    setOtp(''); setNewPw('');
    setView(v);
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(phone, password);
      navigate('/');
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Login failed'));
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPhone(e: FormEvent) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await authApi.forgotPassword(phone);
      setInfo('If that number is registered, you\'ll receive a WhatsApp code shortly.');
      setView('forgot_otp');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp(e: FormEvent) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await authApi.verifyOtp(phone, otp, newPw);
      setAccessToken(data.accessToken);
      connectSocket(data.accessToken);
      useAuthStore.setState({ user: data.user, accessToken: data.accessToken });
      navigate('/');
    } catch (err: unknown) {
      setError(apiErrorMessage(err, 'Invalid code or password too short.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--vbg)',
    }}>
      <div style={{
        width: 380, background: 'var(--vs1)', border: '1px solid var(--vb)',
        borderRadius: 6, padding: '40px 36px',
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
            <img src="/logo.svg" style={{ height: 40, width: 'auto' }} />
            <span style={{ fontSize: '2rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
              <span style={{ color: 'var(--shell-text)' }}>Relay</span><span style={{ color: 'var(--brand-accent)' }}>Through</span>
            </span>
          </div>
          <div style={{ color: 'var(--shell-muted)', fontSize: '0.82rem' }}>
            Client Communication Platform
          </div>
        </div>

        {/* ── LOGIN ── */}
        {view === 'login' && (
          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <label style={labelStyle}>Phone Number or Username</label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                autoFocus
                placeholder="27821234567 or username"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  style={{ ...inputStyle, paddingRight: 36 }}
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} style={eyeBtn}>
                  {showPw ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error && <ErrorBox>{error}</ErrorBox>}

            <button type="submit" disabled={loading} style={submitBtn(loading)}>
              {loading ? 'Signing in…' : 'Sign In'}
            </button>

            <button
              type="button"
              onClick={() => reset('forgot_phone')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--v2)', fontSize: '0.78rem', marginTop: -4 }}
            >
              Forgot password?
            </button>
          </form>
        )}

        {/* ── FORGOT — enter phone ── */}
        {view === 'forgot_phone' && (
          <form onSubmit={handleForgotPhone} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--shell-muted)', marginBottom: 2 }}>
              Reset Password
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--shell-xs)', lineHeight: 1.5 }}>
              Enter your phone number. We'll send a one-time code to your WhatsApp.
            </div>
            <div>
              <label style={labelStyle}>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                autoFocus
                placeholder="27821234567"
                style={inputStyle}
              />
            </div>

            {error && <ErrorBox>{error}</ErrorBox>}

            <button type="submit" disabled={loading} style={submitBtn(loading)}>
              {loading ? 'Sending…' : 'Send Code'}
            </button>
            <button type="button" onClick={() => reset('login')} style={backBtn}>
              Back to sign in
            </button>
          </form>
        )}

        {/* ── FORGOT — enter OTP + new password ── */}
        {view === 'forgot_otp' && (
          <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--shell-muted)', marginBottom: 2 }}>
              Enter Code
            </div>
            {info && (
              <div style={{ fontSize: '0.78rem', color: 'var(--v2)', background: 'rgba(29,158,117,.1)', padding: '8px 10px', borderRadius: 4 }}>
                {info}
              </div>
            )}
            <div>
              <label style={labelStyle}>6-Digit Code</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                required
                autoFocus
                placeholder="123456"
                style={{ ...inputStyle, letterSpacing: '0.2em', fontSize: '1.1rem' }}
              />
            </div>
            <div>
              <label style={labelStyle}>New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNewPw ? 'text' : 'password'}
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  required
                  placeholder="Min. 8 characters"
                  style={{ ...inputStyle, paddingRight: 36 }}
                />
                <button type="button" onClick={() => setShowNewPw((v) => !v)} style={eyeBtn}>
                  {showNewPw ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error && <ErrorBox>{error}</ErrorBox>}

            <button type="submit" disabled={loading} style={submitBtn(loading)}>
              {loading ? 'Saving…' : 'Set New Password'}
            </button>
            <button type="button" onClick={() => reset('forgot_phone')} style={backBtn}>
              Resend code
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 24, color: 'var(--shell-xs)', fontSize: '0.73rem' }}>
          Staff accounts are provisioned by your admin.
        </div>
        <div style={{ textAlign: 'center', marginTop: 12, color: 'var(--shell-xs)', fontSize: '0.68rem', opacity: 0.6 }}>
          © 2026 RelayThrough
        </div>
      </div>
    </div>
  );
}

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      background: 'rgba(239,68,68,.12)', border: '1px solid rgba(239,68,68,.3)',
      borderRadius: 3, padding: '8px 12px', color: 'var(--red)', fontSize: '0.8rem',
    }}>
      {children}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.75rem', color: 'var(--shell-xs)',
  marginBottom: 6, fontWeight: 600,
};
const inputStyle: React.CSSProperties = {
  width: '100%', background: 'var(--vs2)', border: '1px solid var(--vb)',
  borderRadius: 3, padding: '8px 12px', color: 'var(--text)', fontSize: '0.88rem',
  boxSizing: 'border-box',
};
const eyeBtn: React.CSSProperties = {
  position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--shell-xs)', fontSize: '0.9rem', padding: 0, lineHeight: 1,
};
const submitBtn = (loading: boolean): React.CSSProperties => ({
  background: loading ? 'var(--v3)' : 'var(--v1)', color: 'var(--text-on-accent)',
  border: 'none', borderRadius: 3, padding: '10px 0',
  fontWeight: 700, fontSize: '0.88rem', cursor: loading ? 'not-allowed' : 'pointer',
  marginTop: 4, transition: 'background .15s',
});
const backBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: 'var(--shell-xs)', fontSize: '0.78rem', marginTop: -4,
};
